import { useEffect, useState } from 'react';
import { listKnowledge } from '../../api/services/knowledgeService';

/**
 * Publish / unpublish confirmation (FR-BOT-001). Publishing makes the bot answer real visitors, so the dialog
 * lists what goes live and checks it first: blockers stop the publish, warnings are shown but allowed.
 */
export function PublishDialog({ bot, action, busy, onConfirm, onCancel }) {
  const [published, setPublished] = useState(null); // number of published knowledge entries, null = unknown / not allowed to read

  useEffect(() => {
    if (action !== 'publish') return undefined;
    let active = true;
    listKnowledge().then((items) => { if (active) setPublished(items.filter((k) => k.status === 'Published').length); }).catch(() => { if (active) setPublished(null); });
    return () => { active = false; };
  }, [action]);

  if (action === 'unpublish') {
    return (
      <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="pub-title" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
        <div className="modal-card">
          <h3 id="pub-title">Unpublish “{bot.name}”?</h3>
          <p>Visitors will stop getting replies from this bot, and the chat widget will show it as unavailable. Your configuration is kept, and you can publish again at any time.</p>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Keep published</button>
            <button type="button" className="btn-primary" style={{ background: 'var(--danger)' }} onClick={onConfirm} disabled={busy}>{busy ? 'Unpublishing…' : 'Unpublish'}</button>
          </div>
        </div>
      </div>
    );
  }

  const greeting = (bot.greeting || '').trim();
  const fallback = (bot.fallbackMessage || '').trim();
  const checks = [
    { id: 'name', ok: (bot.name || '').trim().length >= 3, level: 'block', text: 'Bot name is set', fix: 'Add a name (at least 3 characters) under Bot Name & Greeting.' },
    { id: 'greeting', ok: greeting.length >= 10, level: 'block', text: 'Greeting message is set', fix: 'Write a greeting of at least 10 characters under Bot Name & Greeting.' },
    { id: 'fallback', ok: fallback.length >= 10, level: 'block', text: 'Fallback message is set', fix: 'Add a fallback message under Fallback Message.' },
    { id: 'handoff', ok: bot.handoffEnabled !== false && bot.handoff?.enabled !== false, level: 'warn', text: 'Human handoff is on', fix: 'Handoff is off, so visitors cannot be passed to an agent.' },
    { id: 'kb', ok: published === null ? true : published > 0, level: 'warn', text: 'At least one published knowledge entry', fix: 'No knowledge entry is published, so answers will rely on general knowledge only.' }
  ];
  const blockers = checks.filter((c) => !c.ok && c.level === 'block');
  const warnings = checks.filter((c) => !c.ok && c.level === 'warn');

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="pub-title" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal-card publish-card">
        <h3 id="pub-title">Publish “{bot.name}”?</h3>
        <p>Visitors on your website will start talking to this bot right away.</p>
        <dl className="publish-summary">
          <div><dt>Greeting</dt><dd>{greeting || '—'}</dd></div>
          <div><dt>Model</dt><dd>{bot.model || '—'}</dd></div>
          <div><dt>Tone</dt><dd>{bot.tone || '—'}</dd></div>
          <div><dt>Language</dt><dd>{bot.primaryLanguage || '—'}</dd></div>
        </dl>
        <ul className="publish-checks" aria-label="Readiness checks">
          {checks.map((c) => (
            <li key={c.id} className={c.ok ? 'ok' : c.level === 'block' ? 'block' : 'warn'}>
              <span aria-hidden="true">{c.ok ? '✓' : c.level === 'block' ? '✕' : '!'}</span>
              <span>{c.ok ? c.text : c.fix}</span>
            </li>
          ))}
        </ul>
        {blockers.length > 0 && <p className="publish-note error" role="alert">Fix the items marked ✕ before publishing.</p>}
        {blockers.length === 0 && warnings.length > 0 && <p className="publish-note">You can still publish, but check the items marked !.</p>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" onClick={onConfirm} disabled={busy || blockers.length > 0}>{busy ? 'Publishing…' : 'Publish bot'}</button>
        </div>
      </div>
    </div>
  );
}
